import React, { useEffect, useState } from 'react';
import apiService from '../services/apiService';
import FlashMessage from './FlashMessage';
import {
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
} from '../styles/SubscriptionFormModalStyles';
import {
  BillsTableWrapper,
  BillsTableEl,
  BillsTableHeader,
  BillsTableRow,
  BillsTableData,
  StatusBadgeBills,
} from '../styles/BillsTableStyles';
import { FormRow, Label, SubmitButton } from '../styles/ComponentStyles';
import { BillNumberInput, BillDateInput } from '../styles/BillsFormStyles';
import { gainLossColor, formatMoney, formatPct } from '../utils/investmentMath';
import { sortByDate } from '../utils/sortByDate';

const emptyDate = () => new Date().toISOString().slice(0, 10);

const InvestmentDetailModal = ({ investment, onClose, onRefresh }) => {
  const [item, setItem] = useState(investment || null);
  const [loading, setLoading] = useState(true);
  const [flashMessage, setFlashMessage] = useState('');
  const [contribSortDir, setContribSortDir] = useState('desc');
  const [valueSortDir, setValueSortDir] = useState('desc');
  const [newContribution, setNewContribution] = useState({
    amount: '',
    date: emptyDate(),
    remarks: '',
  });
  const [newValue, setNewValue] = useState({
    value: '',
    date: emptyDate(),
    remarks: '',
  });

  const setFlash = (msg) => {
    setFlashMessage(msg);
    setTimeout(() => setFlashMessage(''), 3000);
  };

  const fetchItem = async () => {
    if (!investment?._id) return;
    setLoading(true);
    try {
      const res = await apiService.getInvestment(investment._id);
      const detail = res?.data?.item || res?.item || res;
      setItem(detail);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investment?._id]);

  const contributions = item?.contributions || [];
  const values = item?.values || [];
  const toggleContribSortDir = () =>
    setContribSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
  const toggleValueSortDir = () =>
    setValueSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
  const sortedContributions = sortByDate(contributions, 'date', contribSortDir);
  const sortedValues = sortByDate(values, 'date', valueSortDir);

  const handleAddContribution = async (e) => {
    e.preventDefault();
    if (!newContribution.amount || !newContribution.date) {
      setFlash('Please fill amount and date.');
      return;
    }
    try {
      await apiService.recordContribution(investment._id, {
        amount: parseFloat(newContribution.amount),
        date: newContribution.date,
        remarks: newContribution.remarks || null,
      });
      setFlash('Contribution added.');
      setNewContribution({ amount: '', date: emptyDate(), remarks: '' });
      await fetchItem();
      if (onRefresh) onRefresh();
    } catch (err) {
      setFlash(err?.response?.data?.detail || err?.message || 'Failed to add contribution.');
    }
  };

  const handleDeleteContribution = async (id) => {
    try {
      await apiService.deleteContribution(investment._id, id);
      setFlash('Contribution deleted.');
      await fetchItem();
      if (onRefresh) onRefresh();
    } catch (err) {
      setFlash(err?.response?.data?.detail || 'Failed to delete.');
    }
  };

  const handleAddValue = async (e) => {
    e.preventDefault();
    if (!newValue.value || !newValue.date) {
      setFlash('Please fill value and date.');
      return;
    }
    try {
      await apiService.recordValue(investment._id, {
        value: parseFloat(newValue.value),
        date: newValue.date,
        remarks: newValue.remarks || null,
      });
      setFlash('Value recorded.');
      setNewValue({ value: '', date: emptyDate(), remarks: '' });
      await fetchItem();
      if (onRefresh) onRefresh();
    } catch (err) {
      setFlash(err?.response?.data?.detail || err?.message || 'Failed to record value.');
    }
  };

  const handleDeleteValue = async (id) => {
    try {
      await apiService.deleteValue(investment._id, id);
      setFlash('Value deleted.');
      await fetchItem();
      if (onRefresh) onRefresh();
    } catch (err) {
      setFlash(err?.response?.data?.detail || 'Failed to delete.');
    }
  };

  const deleteBtnStyle = {
    background: 'none',
    border: 'none',
    color: '#f44336',
    cursor: 'pointer',
  };

  const gl = item?.gain_loss;

  return (
    <ModalBackdrop onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
        <ModalHeader>
          <ModalTitle>{item?.name || 'Investment'}</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            ×
          </CloseButton>
        </ModalHeader>
        {flashMessage && <FlashMessage message={flashMessage} />}

        {/* Gain/loss summary */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            padding: '12px 0',
            borderBottom: '1px solid #555',
            marginBottom: 12,
          }}
        >
          <div>
            <div style={{ color: '#a0a0a0', fontSize: '0.8rem' }}>Total Contributed</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {formatMoney(item?.total_contributed)}
            </div>
          </div>
          <div>
            <div style={{ color: '#a0a0a0', fontSize: '0.8rem' }}>Latest Value</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {item?.latest_value != null ? formatMoney(item.latest_value) : '—'}
              {item?.latest_value_date ? (
                <span style={{ color: '#888', fontSize: '0.75rem', marginLeft: 6 }}>
                  ({item.latest_value_date})
                </span>
              ) : null}
            </div>
          </div>
          <div>
            <div style={{ color: '#a0a0a0', fontSize: '0.8rem' }}>Gain / Loss</div>
            <div>
              {gl != null ? (
                <StatusBadgeBills $color={gainLossColor(gl)}>
                  {gl >= 0 ? '+' : ''}
                  {formatMoney(gl)}
                  {item?.gain_loss_pct != null ? ` (${formatPct(item.gain_loss_pct)})` : ''}
                </StatusBadgeBills>
              ) : (
                <StatusBadgeBills $color={gainLossColor(null)}>NO VALUE YET</StatusBadgeBills>
              )}
            </div>
          </div>
        </div>

        {loading && <p>Loading...</p>}

        {/* Contributions */}
        <form onSubmit={handleAddContribution}>
          <h4 style={{ marginBottom: 12 }}>Add contribution</h4>
          <FormRow>
            <Label>Date</Label>
            <BillDateInput
              name="date"
              value={newContribution.date}
              onChange={(e) =>
                setNewContribution((p) => ({ ...p, date: e.target.value }))
              }
              required
            />
          </FormRow>
          <FormRow>
            <Label>Amount</Label>
            <BillNumberInput
              name="amount"
              min="0"
              value={newContribution.amount}
              onChange={(e) =>
                setNewContribution((p) => ({ ...p, amount: e.target.value }))
              }
              required
            />
          </FormRow>
          <FormRow>
            <Label>Remarks (optional)</Label>
            <input
              name="remarks"
              value={newContribution.remarks}
              onChange={(e) =>
                setNewContribution((p) => ({ ...p, remarks: e.target.value }))
              }
              style={{
                width: '100%',
                maxWidth: 400,
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid #555',
                backgroundColor: '#2e2e2e',
                color: '#e0e0e0',
              }}
            />
          </FormRow>
          <SubmitButton type="submit">Add Contribution</SubmitButton>
        </form>

        <h4 style={{ marginTop: 24, marginBottom: 8 }}>Contributions</h4>
        <BillsTableWrapper style={{ marginTop: 8 }}>
          <BillsTableEl style={{ minWidth: 480 }}>
            <thead>
              <tr>
                <BillsTableHeader
                  onClick={toggleContribSortDir}
                  title="Sort by date"
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Date {contribSortDir === 'desc' ? '▼' : '▲'}
                </BillsTableHeader>
                <BillsTableHeader>Amount</BillsTableHeader>
                <BillsTableHeader>Remarks</BillsTableHeader>
                <BillsTableHeader>Actions</BillsTableHeader>
              </tr>
            </thead>
            <tbody>
              {sortedContributions.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: 16, color: '#888' }}>
                    No contributions yet. Add one above.
                  </td>
                </tr>
              ) : (
                sortedContributions.map((c) => (
                  <BillsTableRow key={c.id || c.date + c.amount}>
                    <BillsTableData>{c.date}</BillsTableData>
                    <BillsTableData>{formatMoney(c.amount)}</BillsTableData>
                    <BillsTableData>{c.remarks || '—'}</BillsTableData>
                    <BillsTableData>
                      <button
                        type="button"
                        onClick={() => handleDeleteContribution(c.id)}
                        style={deleteBtnStyle}
                      >
                        Delete
                      </button>
                    </BillsTableData>
                  </BillsTableRow>
                ))
              )}
            </tbody>
          </BillsTableEl>
        </BillsTableWrapper>

        {/* Value history */}
        <form onSubmit={handleAddValue}>
          <h4 style={{ marginTop: 24, marginBottom: 12 }}>Record current value</h4>
          <FormRow>
            <Label>Date</Label>
            <BillDateInput
              name="date"
              value={newValue.date}
              onChange={(e) => setNewValue((p) => ({ ...p, date: e.target.value }))}
              required
            />
          </FormRow>
          <FormRow>
            <Label>Value</Label>
            <BillNumberInput
              name="value"
              min="0"
              value={newValue.value}
              onChange={(e) => setNewValue((p) => ({ ...p, value: e.target.value }))}
              required
            />
          </FormRow>
          <FormRow>
            <Label>Remarks (optional)</Label>
            <input
              name="remarks"
              value={newValue.remarks}
              onChange={(e) => setNewValue((p) => ({ ...p, remarks: e.target.value }))}
              style={{
                width: '100%',
                maxWidth: 400,
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid #555',
                backgroundColor: '#2e2e2e',
                color: '#e0e0e0',
              }}
            />
          </FormRow>
          <SubmitButton type="submit">Record Value</SubmitButton>
        </form>

        <h4 style={{ marginTop: 24, marginBottom: 8 }}>Value history</h4>
        <BillsTableWrapper style={{ marginTop: 8 }}>
          <BillsTableEl style={{ minWidth: 480 }}>
            <thead>
              <tr>
                <BillsTableHeader
                  onClick={toggleValueSortDir}
                  title="Sort by date"
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Date {valueSortDir === 'desc' ? '▼' : '▲'}
                </BillsTableHeader>
                <BillsTableHeader>Value</BillsTableHeader>
                <BillsTableHeader>Remarks</BillsTableHeader>
                <BillsTableHeader>Actions</BillsTableHeader>
              </tr>
            </thead>
            <tbody>
              {sortedValues.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: 16, color: '#888' }}>
                    No value records yet. Add one above.
                  </td>
                </tr>
              ) : (
                sortedValues.map((v) => (
                  <BillsTableRow key={v.id || v.date + v.value}>
                    <BillsTableData>{v.date}</BillsTableData>
                    <BillsTableData>{formatMoney(v.value)}</BillsTableData>
                    <BillsTableData>{v.remarks || '—'}</BillsTableData>
                    <BillsTableData>
                      <button
                        type="button"
                        onClick={() => handleDeleteValue(v.id)}
                        style={deleteBtnStyle}
                      >
                        Delete
                      </button>
                    </BillsTableData>
                  </BillsTableRow>
                ))
              )}
            </tbody>
          </BillsTableEl>
        </BillsTableWrapper>

        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: 16,
            padding: '8px 16px',
            background: '#555',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </ModalContent>
    </ModalBackdrop>
  );
};

export default InvestmentDetailModal;
